<template>
  <div>
    <div class="flex items-center gap-3 mb-4">
      <span class="text-gray-600">{{ cases.length }} cases</span>
      <span v-if="dirty" class="text-gray-900">· unsaved changes</span>
      <div class="ml-auto flex items-center gap-3">
        <button type="button" :class="TEXT_BUTTON" @click="addCase">Add case</button>
        <button type="button" :class="TEXT_BUTTON" @click="showImport = !showImport">Import JSON</button>
        <button type="button" :class="TEXT_BUTTON" @click="exportJson">Export JSON</button>
        <button type="button" :class="TEXT_BUTTON" :disabled="!dirty" @click="reset">Discard</button>
        <button type="button" :class="PRIMARY_BUTTON" :disabled="!dirty || saving" @click="save">{{ saving ? 'Saving…' : 'Save cases' }}</button>
      </div>
    </div>
    <p v-if="saveError" class="text-red-600 mb-3">{{ saveError }}</p>

    <div v-if="showImport" class="rounded-md border border-gray-200 p-4 mb-4">
      <span class="block text-gray-500 mb-2">A JSON list of cases: {label, turns, answers?, hats?, expect?, rubric?}. Replaces the whole list; save afterwards.</span>
      <textarea v-model="importText" :class="[INPUT, 'font-mono h-40']" />
      <div class="flex items-center gap-3 mt-2">
        <button type="button" :class="TEXT_BUTTON" @click="importJson">Replace list</button>
        <span v-if="importError" class="text-red-600">{{ importError }}</span>
      </div>
    </div>

    <div class="rounded-md border border-gray-200 overflow-hidden">
      <table class="w-full table-fixed">
        <colgroup><col style="width:4%"><col style="width:22%"><col style="width:34%"><col style="width:14%"><col style="width:14%"><col style="width:12%"></colgroup>
        <thead class="bg-gray-50"><tr>
          <th :class="TH">#</th><th :class="TH">Case</th><th :class="TH">First turn</th><th :class="TH">Checks</th><th :class="TH">Hat path</th><th :class="[TH, 'text-right']">Actions</th>
        </tr></thead>
        <tbody class="divide-y divide-gray-200">
          <tr v-if="!cases.length"><td colspan="6" class="px-3 py-8 text-center text-gray-500">No cases yet. Make one from a turn on the Console tab.</td></tr>
          <tr v-for="(c, i) in cases" :key="i">
            <td :class="[TD, 'text-gray-500 tabular-nums']">{{ i + 1 }}</td>
            <td :class="TD"><span class="font-medium">{{ c.label }}</span><span class="block text-gray-500">{{ c.tenant || bench.tenantName }}<template v-if="c.turns.length > 1"> · {{ c.turns.length }} turns</template></span></td>
            <td :class="TD"><span class="block truncate" :title="c.turns[0]">{{ c.turns[0] || '—' }}</span></td>
            <td :class="TD"><span class="text-gray-700">{{ checkSummary(c) }}</span></td>
            <td :class="TD"><span class="text-gray-700 truncate block">{{ c.hats || '—' }}</span></td>
            <td :class="[TD, 'text-right whitespace-nowrap']">
              <button type="button" :class="TEXT_BUTTON" @click="edit(i)">Edit</button>
              <button type="button" :class="[TEXT_BUTTON, 'ml-3']" :disabled="i === 0" @click="move(i, -1)">Up</button>
              <button type="button" :class="[TEXT_BUTTON, 'ml-3']" :disabled="i === cases.length - 1" @click="move(i, 1)">Down</button>
              <button type="button" :class="[TEXT_BUTTON, 'ml-3']" @click="remove(i)">Remove</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="editing !== null" class="fixed inset-0 z-40 bg-black/30 flex items-center justify-center" @click.self="editing = null">
      <div class="bg-white rounded-lg border border-gray-200 w-[56rem] max-w-[95vw] max-h-[90vh] flex flex-col">
        <div class="px-5 py-3 border-b border-gray-100 flex items-center">
          <h3 class="font-semibold text-gray-900">{{ draft.label || 'New case' }}</h3>
          <button class="ml-auto text-gray-400 hover:text-gray-600" @click="editing = null">×</button>
        </div>
        <div class="p-5 overflow-y-auto grid grid-cols-12 gap-4">
          <label class="col-span-5"><span class="block text-gray-500 mb-2">Label</span><input v-model="draft.label" :class="INPUT"></label>
          <label class="col-span-2"><span class="block text-gray-500 mb-2">Tenant</span><input v-model="draft.tenant" :class="INPUT" placeholder="vytalize"></label>
          <label class="col-span-5"><span class="block text-gray-500 mb-2">Required hat path (optional, comma-separated)</span><input v-model="draft.hats" :class="INPUT" placeholder="population,practice_performance,reporter"></label>
          <label class="col-span-8">
            <span class="block text-gray-500 mb-2">What the user says, one turn per line, played in order</span>
            <textarea v-model="draft.turnsText" :class="[INPUT, 'h-28']" />
          </label>
          <label class="col-span-4">
            <span class="block text-gray-500 mb-2">Answers if the agent asks, one per line</span>
            <textarea v-model="draft.answersText" :class="[INPUT, 'h-28']" placeholder="1" />
          </label>
          <label class="col-span-12"><span class="block text-gray-500 mb-2">Rubric for the judge, one sentence on what a good answer does</span><input v-model="draft.rubric" :class="INPUT"></label>
          <fieldset class="col-span-12 grid grid-cols-12 gap-4">
            <legend class="col-span-12 text-gray-500">Checks the log must satisfy (comma-separated lists; leave blank to skip a check)</legend>
            <label class="col-span-6"><span class="block text-gray-500 mb-2">Must call one of these tools</span><input v-model="draft.tools_any" :class="INPUT" placeholder="query_snowflake, search_library"></label>
            <label class="col-span-6"><span class="block text-gray-500 mb-2">Must never call</span><input v-model="draft.tools_never" :class="INPUT" placeholder="save_page, create_metric"></label>
            <label class="col-span-6"><span class="block text-gray-500 mb-2">Must touch one of these tables</span><input v-model="draft.tables_any" :class="INPUT" placeholder="EMPI.CONTRACT_COHORT_MEMBER"></label>
            <label class="col-span-6"><span class="block text-gray-500 mb-2">Must never touch</span><input v-model="draft.tables_never" :class="INPUT" placeholder="SOURCE_DATA_CCLF.CCLF_BENE_FULL"></label>
            <label class="col-span-4"><span class="block text-gray-500 mb-2">Should wear one of these hats</span><input v-model="draft.hats_expect" :class="INPUT" placeholder="population, cost"></label>
            <label class="col-span-4"><span class="block text-gray-500 mb-2">Reply must mention</span><input v-model="draft.mentions" :class="INPUT"></label>
            <label class="col-span-2"><span class="block text-gray-500 mb-2">Max tool calls</span><input v-model.number="draft.max_tool_calls" type="number" min="1" :class="INPUT"></label>
            <label class="col-span-2">
              <span class="block text-gray-500 mb-2">Writes</span>
              <select v-model="draft.writes" :class="INPUT"><option value="">any</option><option value="none">none allowed</option><option value="list">must propose…</option></select>
            </label>
            <label v-if="draft.writes === 'list'" class="col-span-12"><span class="block text-gray-500 mb-2">Bridge kinds that must be proposed</span><input v-model="draft.writes_list" :class="INPUT" placeholder="update_field"></label>
          </fieldset>
          <p class="col-span-12 text-gray-500">Two checks always run: no patient identifiers in the reply text, and no error events in the session. The bench never approves a write; a proposal is recorded and declined.</p>
        </div>
        <div class="px-5 py-3 border-t border-gray-100 flex items-center gap-3">
          <span v-if="draftError" class="text-red-600">{{ draftError }}</span>
          <button type="button" :class="[SECONDARY_BUTTON, 'ml-auto']" @click="editing = null">Cancel</button>
          <button type="button" :class="PRIMARY_BUTTON" @click="applyDraft">Done</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH, errorOf } from '../hermes';
import { caseFrom, casesOf, saveCases } from './bench';

const LISTS = ['tools_any', 'tools_never', 'tables_any', 'tables_never', 'mentions'];

function toDraft(c) {
  const e = c.expect || {};
  const d = {
    label: c.label, hats: c.hats || '', rubric: c.rubric || '', tenant: c.tenant || '', source_session: c.source_session || '',
    turnsText: (c.turns || []).join('\n'), answersText: (c.answers || []).join('\n'),
    hats_expect: (e.hats || []).join(', '), max_tool_calls: e.max_tool_calls || null,
    writes: e.writes === 'none' ? 'none' : Array.isArray(e.writes) ? 'list' : '',
    writes_list: Array.isArray(e.writes) ? e.writes.join(', ') : '',
  };
  LISTS.forEach((k) => { d[k] = (e[k] || []).join(', '); });
  return d;
}

function split(text) {
  return String(text || '').split(',').map((s) => s.trim()).filter(Boolean);
}

function fromDraft(d) {
  const expect = {};
  LISTS.forEach((k) => { const v = split(d[k]); if (v.length) expect[k] = v; });
  const hats = split(d.hats_expect);
  if (hats.length) expect.hats = hats;
  if (d.max_tool_calls) expect.max_tool_calls = Number(d.max_tool_calls);
  if (d.writes === 'none') expect.writes = 'none';
  else if (d.writes === 'list' && split(d.writes_list).length) expect.writes = split(d.writes_list);
  return caseFrom({
    label: d.label.trim(), hats: d.hats.trim(), rubric: d.rubric.trim(), expect,
    tenant: String(d.tenant || '').trim(), source_session: d.source_session || '',
    turns: String(d.turnsText || '').split('\n').map((s) => s.trim()).filter(Boolean),
    answers: String(d.answersText || '').split('\n').map((s) => s.trim()).filter(Boolean),
  });
}

export default {
  name: 'TestCases',
  props: { bench: { type: Object, required: true } },
  emits: ['refresh'],
  data() {
    const cases = casesOf(this.bench.template);
    return {
      cases, original: JSON.stringify(cases), saving: false, saveError: '',
      showImport: false, importText: '', importError: '', editing: null, draft: toDraft(caseFrom({})), draftError: '',
      INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH,
    };
  },
  computed: {
    dirty() {
      return JSON.stringify(this.cases) !== this.original;
    },
  },
  methods: {
    plain(c) {
      const copy = { ...c };
      delete copy.kind;
      return copy;
    },
    checkSummary(c) {
      const e = c.expect || {};
      const n = Object.keys(e).length;
      return n ? `${n} named + 2 standard` : '2 standard';
    },
    addCase() {
      this.cases.push(caseFrom({ label: `Case ${this.cases.length + 1}` }));
      this.edit(this.cases.length - 1);
    },
    edit(i) {
      this.draft = toDraft(this.cases[i]);
      this.draftError = '';
      this.editing = i;
    },
    applyDraft() {
      const c = fromDraft(this.draft);
      if (!c.label) { this.draftError = 'A case needs a label.'; return; }
      if (!c.turns.length) { this.draftError = 'A case needs at least one user turn.'; return; }
      this.cases.splice(this.editing, 1, c);
      this.editing = null;
    },
    move(i, dir) {
      const [item] = this.cases.splice(i, 1);
      this.cases.splice(i + dir, 0, item);
    },
    remove(i) {
      this.cases.splice(i, 1);
    },
    reset() {
      this.cases = JSON.parse(this.original);
    },
    importJson() {
      this.importError = '';
      let list;
      try {
        list = JSON.parse(this.importText);
      } catch (e) {
        this.importError = `Not valid JSON: ${e.message}`;
        return;
      }
      if (!Array.isArray(list) || !list.length) { this.importError = 'Expected a non-empty JSON list.'; return; }
      this.cases = list.map(caseFrom);
      this.showImport = false;
      this.importText = '';
    },
    exportJson() {
      const text = JSON.stringify(this.cases.map((c) => this.plain(c)), null, 2);
      const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'agent_bench_cases.json';
      a.click();
      URL.revokeObjectURL(url);
    },
    async save() {
      this.saving = true;
      this.saveError = '';
      try {
        await saveCases(this.$axios, this.bench, this.cases);
        this.original = JSON.stringify(this.cases);
        this.$emit('refresh');
      } catch (e) {
        this.saveError = errorOf(e, 'The cases were not saved.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
